// @ts-check
import { io } from 'socket.io-client';
import { WS_LINK } from '../../../config';
import { getCurrentUser, refreshAuthToken } from '../../api/client';
import { RealtimeClient } from './realtimeClient';

/** Support rooms are joined with the support engineer's session, not the restaurant user's. */
const isSupportRoom = (room) => room === 'support:tickets' || String(room).startsWith('support-ticket:');

/** Rooms notify only opens to a credential: never join them before one exists. */
const isProtectedRoom = (room) => isSupportRoom(room) || String(room).startsWith('admin-restaurant:');

const supportToken = () => {
  try {
    return JSON.parse(localStorage.getItem('supportUserObj') || '{}')?.access_token || null;
  } catch {
    return null;
  }
};

/**
 * WebSocket service - one shared entry point for the notify relay.
 *
 * Rooms are ref-counted (join/release), several rooms can be joined at once,
 * listeners are never wiped when a room changes, reconnection never gives up,
 * and missed events are replayed (or the room's resync hooks refetch).
 * See realtimeClient.js for the mechanics.
 *
 *   const release = wsService.join(`admin-restaurant:${rid}`, { onResync: refetch });
 *   const off = wsService.on('order::new', handler, { room: `admin-restaurant:${rid}` });
 *   // cleanup: off(); release();
 */
class WebSocketService {
  constructor({ client } = {}) {
    this.client = client || new RealtimeClient({
      url: WS_LINK,
      io,
      getAuth: (room) => {
        const token = isSupportRoom(room) ? supportToken() : getCurrentUser().access_token;
        return token ? { token } : {};
      },
      // One connection per credential: every restaurant room (and order-session
      // room) of the tab shares the user's connection, support rooms use the
      // support engineer's.
      authGroup: (room) => (isSupportRoom(room) ? 'support' : 'user'),
      // No token-less join (seen at admin load): wait for the token instead.
      requiresAuth: isProtectedRoom,
      // join::denied (token expired while the tab was open): refresh, then the
      // client reconnects with the new token. The support session has no refresh.
      refreshAuth: async (room) => {
        if (isSupportRoom(room)) return false;
        await refreshAuthToken();
        return true;
      },
    });
    /** @type {(() => void) | null} */
    this.switchRelease = null;
    this.switchRoomName = null;
  }

  /**
   * Join a room (ref-counted). Call the returned function to release it.
   * @param {string} room e.g. 'admin-restaurant:123', 'enduser-restaurant:<session>', 'support:tickets'
   * @param {{onResync?: (info: {room: string, reason: string}) => void}} [opts]
   *        onResync: events may have been missed (reconnect without full replay) - refetch
   * @returns {() => void}
   */
  join(room, opts) {
    return this.client.join(room, opts);
  }

  /**
   * Backward-compatible alias of join(). Returns the release function
   * (callers that await it get the same function).
   * @param {string} room
   * @param {{onResync?: Function}} [opts]
   */
  connect(room, opts) {
    return this.join(room, /** @type {any} */ (opts));
  }

  /**
   * Subscribe to an event. Without opts.room the callback hears every joined room.
   * Callback: (data, {room, seq, replayed}).
   * @param {string} event
   * @param {Function} callback
   * @param {{room?: string | null}} [opts]
   * @returns {() => void} unsubscribe
   */
  on(event, callback, opts) {
    return this.client.on(event, callback, opts);
  }

  /** @param {string} event @param {Function} callback */
  off(event, callback) {
    this.client.off(event, callback);
  }

  /**
   * Emit to the server (on the room's connection when given).
   * @param {string} event
   * @param {any} data
   * @param {string | null} [room]
   */
  emit(event, data, room = null) {
    return this.client.emit(event, data, room);
  }

  /**
   * The stored credential changed (login, token refresh, support session):
   * rooms waiting for a token join now.
   */
  authChanged() {
    this.client.authChanged();
  }

  /** Leave every room now and close the connections (logout). Listeners are kept. */
  disconnect() {
    this.switchRelease = null;
    this.switchRoomName = null;
    this.client.closeAll();
  }

  /** @param {string | null} [room] */
  isConnected(room = null) {
    return this.client.isConnected(room);
  }

  /** First joined admin room (legacy single-room API). */
  getCurrentRoom() {
    const rooms = this.client.rooms();
    return rooms.find((r) => r.startsWith('admin-restaurant:')) || rooms[0] || null;
  }

  getRooms() {
    return this.client.rooms();
  }

  /**
   * Dispatch an event locally to all registered listeners WITHOUT a server round-trip.
   * Intended for tests/dev only: lets E2E specs simulate inbound socket events
   * (e.g. `guest_welcome`) deterministically without standing up the notify service.
   * @param {string} event
   * @param {any} data
   * @param {string | null} [room] deliver as if it came from this room (default: all listeners)
   */
  dispatchLocal(event, data, room = null) {
    this.client.dispatch(room, event, data, { room, seq: null, replayed: false, local: true });
  }

  /**
   * Legacy: keep exactly one room joined through this method.
   * @param {string} newRoom
   */
  async switchRoom(newRoom) {
    if (this.switchRoomName === newRoom) return;
    const previous = this.switchRelease;
    this.switchRoomName = newRoom;
    this.switchRelease = newRoom ? this.join(newRoom) : null;
    if (previous) previous();
  }
}

// Export singleton instance
export const wsService = new WebSocketService();

// Expose the singleton for E2E/dev tooling so specs can simulate inbound socket
// events via `window.__odsWsService.dispatchLocal(event, payload)`. Guarded to
// non-production builds to avoid leaking internals in production bundles.
if (typeof window !== 'undefined' && import.meta?.env?.MODE !== 'production') {
  // @ts-ignore - test/dev hook only
  window.__odsWsService = wsService;
}

// Export class for testing
export { WebSocketService };
